import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography } from '@material-ui/core';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';

const AddressCard: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState({
    street: '',
    city: '',
    zipCode: '',
    country: '',
    streetNumber: '',
  });
  const [isMounted, setIsMounted] = useState(true);

  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track organization
  useEffect(() => {
    resetInput();
  }, [organization]);

  const resetInput = () => {
    if (organization?.addressInformation) {
      setInput({
        street: organization.addressInformation.street ?? '',
        city: organization.addressInformation.city ?? '',
        zipCode: organization.addressInformation.zipCode ?? '',
        country: organization.addressInformation.country ?? '',
        streetNumber: organization.addressInformation.streetNumber ?? '',
      });
    } else {
      setInput({
        street: '',
        city: '',
        zipCode: '',
        country: '',
        streetNumber: '',
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput((input) => ({ ...input, [name]: value }));
  };

  const handleSave = () => {
    if (organization) {
      setIsLoading(true);
      dispatch(
        organizationActions.updateOrganizationAddressDetails(
          organization.organizationId,
          input,
          (error) => {
            if (isMounted) {
              setIsLoading(false);
              setEditMode(!!error);
            }
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            }
          },
        ),
      );
    }
  };

  const handleEdit = () => {
    resetInput();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      title={t('organization_page.organization_information.labels.address')}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.street',
              )}
              name="street"
              value={input.street}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.street_number',
              )}
              name="streetNumber"
              value={input.streetNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.city',
              )}
              name="city"
              value={input.city}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.zip_code',
              )}
              name="zipCode"
              value={input.zipCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.country',
              )}
              name="country"
              value={input.country}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Typography variant="body1">
            {input.street}&nbsp;
            {input.streetNumber}
          </Typography>
          <Typography variant="body1">
            {input.zipCode}&nbsp;
            {input.city}
          </Typography>
          <Typography variant="body1">{input.country}</Typography>
        </>
      )}
    </EditCard>
  );
};

export default AddressCard;
